/**
 * @file What one form field shows and how it is marked while a restore point is
 * previewed (SYD-2450). Two consumers, each taking one half: `FieldUI` joins
 * `differs` into the tint class on the memoized `ControlUI`, and `useFieldUI`
 * layers `retained` over its `externalValue` so the control renders the value the
 * version actually held rather than today's.
 *
 * Both halves ride one frozen object per state, never a literal built in the
 * render body: `FieldUI`'s memo contract needs referentially stable props, and
 * `retained` is handed straight off the `useSnapshot` of the read result, which
 * Valtio keeps identical for as long as the result is.
 *
 * Built like `useFormDeriveFieldState`: a per-field-id hook reading the store
 * directly, rather than a prop threaded through `LayoutUI` and `ContainerUI`.
 * Both leaves are read before the branch, so a field outside the diff subscribes
 * to exactly what a field inside it does.
 *
 * Entity references have no tint seam and no value seam — `EntityUI` forwards only
 * `disabled` — so `diff` entries of kind `entityReference` are named in the group
 * and the banner and are invisible here.
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';

import type { FieldId, FormPitrValue } from '#types';

import { formRecoveryStore } from '../../store';

export type FormRecoveryFieldState = {
  /** The retained value at the previewed moment differs from the live one. */
  differs: boolean;
  /**
   * What the field held at that moment, and what a preview must display in place
   * of the live value. Meaningful only while `differs` is true: a field outside
   * the diff held exactly what it holds now, so the live value already IS the
   * retained one. `null` is a key absent at that moment and renders empty, which
   * a key present and cleared (`{ value: null }`) also does — the two are told
   * apart by a restore, never by a reader.
   */
  retained: FormPitrValue | null;
};

/** Not previewing, or this field is not in the diff. */
const absentFormRecoveryFieldState: FormRecoveryFieldState = Object.freeze({
  differs: false,
  retained: null,
});

/**
 * One field's recovery state. `fieldId` is a form field: a point-in-time read
 * covers `FormVersion.fields`, so this says nothing about an entity record.
 */
export const useFormRecoveryFieldState = (fieldId: FieldId): FormRecoveryFieldState => {
  const snap = useSnapshot(formRecoveryStore.state);
  const previewing = snap.previewing;
  const result = snap.result;

  const entry =
    !previewing || result === null || result.outcome !== 'version'
      ? undefined
      : result.diff.find((candidate) => candidate.kind === 'field' && candidate.fieldId === fieldId);

  return useMemo(
    () =>
      entry === undefined
        ? absentFormRecoveryFieldState
        : Object.freeze({ differs: true, retained: entry.retained }),
    [entry],
  );
};
